import type { ReactNode } from "react";

import { AnserMark } from "@/components/ui/anser-mark";
import { cn } from "@/lib/utils";

interface ChatBubbleProps {
  variant: "user" | "anser";
  children: ReactNode;
  className?: string;
}

/**
 * One message bubble in a chat transcript (GRA-199, GAP.md §5): the user
 * variant is accent-filled with a radius of 18/18/4/18 (the corner nearest
 * the sender's own messages stays sharp); the Anser variant is a white
 * surface next to the Anser mark, radius 4/18/18/18. Tokens only.
 */
export function ChatBubble({ variant, children, className }: ChatBubbleProps) {
  if (variant === "user") {
    return (
      <div className="flex justify-end">
        <div
          data-testid="chat-bubble"
          data-variant="user"
          className={cn(
            "max-w-[85%] whitespace-pre-wrap rounded-[18px_18px_4px_18px] bg-g-primary px-4 py-2.5 text-sm text-g-on-primary",
            className,
          )}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5">
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-g-sm bg-g-primary-tint"
      >
        <AnserMark size={18} />
      </span>
      <div
        data-testid="chat-bubble"
        data-variant="anser"
        className={cn(
          "min-w-0 flex-1 rounded-[4px_18px_18px_18px] bg-g-bg-card p-4 shadow-g-card",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
